import {
  Badge,
  Button,
  Card,
  Segmented,
  Select,
  Table,
  Tag,
  Typography,
  Input,
  DatePicker,
  Statistic,
  Divider,
  Form,
  Space,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QuotationService } from "../../Services/Manage/QuotationService";
import useMessage from "antd/es/message/useMessage";
import { UserService } from "../../Services/Manage/UserService";
import dayjs from "dayjs";

import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";
import DashboardSummary from "./DashboardSummary";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);


const QuotationPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [segment, setSegment] = useState("Active");
  const [message, msgContext] = useMessage();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("updated_on");


  /** 🔹 Fetch all quotations */
  const getAllQuo = async () => {
    try {
      const user = await UserService.getOmeUser();
      const requests = await QuotationService.getAllQuotationByUID(user?.uid);
      const allData = requests.data || [];
      setData(allData);
      applyAllFilters(allData, segment, searchText, dateRange, dateFilterType);
    } catch (error) {
      console.error("Error fetching quotations:", error);
    }
  };

  useEffect(() => {
    getAllQuo();
  }, []);

  /** 🔹 Apply filtering */
  const applyAllFilters = (allData, seg, search, range, dateType) => {
    let result = allData;

    // Filter by segment
    if (seg) {
      result = result.filter(
        (item) => item.status?.toLowerCase() === seg.toLowerCase()
      );
    }

    // Filter by search
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.customer_name?.toLowerCase().includes(lower) ||
          item.qu_no?.toLowerCase().includes(lower) ||
          item.eq_no?.toLowerCase().includes(lower) ||
          item.customer_phone?.toString().toLowerCase().includes(lower)
      );
    }

    // Filter by date range (Quotation Date, Added On, or Updated On)
    if (range && range.length === 2) {
      const [start, end] = range;
      result = result.filter((item) => {
        const selectedDate = new Date(item[dateType]); // 👈 Dynamic field
        if (isNaN(selectedDate)) return false;
        return (
          selectedDate >= start.startOf("day").toDate() &&
          selectedDate <= end.endOf("day").toDate()
        );
      });
    }

    // Sort by updated_on (latest first)
    result.sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on));
    setFilteredData(result);
  };

  /** 🔹 Handlers */
  const handleSegmentChange = (val) => {
    setSegment(val);
    applyAllFilters(data, val, searchText, dateRange, dateFilterType);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    applyAllFilters(data, segment, value, dateRange, dateFilterType);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    applyAllFilters(data, segment, searchText, range, dateFilterType);
  };

  const handleDateFilterTypeChange = (value) => {
    setDateFilterType(value);
    applyAllFilters(data, segment, searchText, dateRange, value);
  };

  const applyFollowUpDateFilter = (days) => {
    const start = dayjs().startOf("day");
    const end =
      days === 0
        ? dayjs().endOf("day")
        : dayjs().add(days, "day").endOf("day");

    const range = [start, end];

    setDateFilterType("n_follow_up");
    setDateRange(range);

    applyAllFilters(data, segment, searchText, range, "n_follow_up");
  };


  /** 🔹 Status counts */
  const statusCounts = data.reduce((acc, cur) => {
    const key = cur.status || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const styles = {
    badge: {
      backgroundColor: "#1b1660ff",
      fontSize: 14,
      minWidth: 24,
      height: 24,
      lineHeight: "24px",
      marginLeft: 10,
    },
  };

  const getQuickStats = (status) => {
    const today = dayjs();
    const startToday = today.startOf("day");

    const last7 = today.subtract(6, "day").startOf("day");
    const last30 = today.subtract(29, "day").startOf("day");

    // 👇 filter by selected status first
    const statusData = data.filter(
      d => d.status?.toLowerCase() === status.toLowerCase()
    );

    return {
      today: statusData.filter(d =>
        d.added_on && dayjs(d.added_on).isAfter(startToday)
      ).length,

      last7: statusData.filter(d =>
        d.added_on && dayjs(d.added_on).isAfter(last7)
      ).length,

      last30: statusData.filter(d =>
        d.added_on && dayjs(d.added_on).isAfter(last30)
      ).length,

      total: statusData.length,
    };
  };

  const getFollowUpStats = (status) => {
    const today = dayjs().startOf("day");
    const endToday = dayjs().endOf("day");

    const next7 = dayjs().add(7, "day").endOf("day");
    const next30 = dayjs().add(30, "day").endOf("day");

    const statusData = data.filter(
      d => d.status?.toLowerCase() === status.toLowerCase()
    );

    return {
      today: statusData.filter(d =>
        d.n_follow_up &&
        dayjs(d.n_follow_up).isBetween(today, endToday, null, "[]")
      ).length,

      next7: statusData.filter(d =>
        d.n_follow_up &&
        dayjs(d.n_follow_up).isAfter(today) &&
        (
          dayjs(d.n_follow_up).isBefore(next7) ||
          dayjs(d.n_follow_up).isSame(next7)
        )
      ).length,


      next30: statusData.filter(d =>
        d.n_follow_up &&
        dayjs(d.n_follow_up).isAfter(today) &&
        dayjs(d.n_follow_up).isSameOrBefore(next30)
      ).length,

      total: statusData.filter(d => d.n_follow_up).length,
    };
  };


  const quickStats = getQuickStats(segment);

  const followUpStats = getFollowUpStats(segment);


  const applyQuickDateFilter = (days) => {
    const end = dayjs();
    const start =
      days === 0
        ? end.startOf("day")
        : end.subtract(days - 1, "day").startOf("day");

    const range = [start, end];

    setDateFilterType("added_on");
    setDateRange(range);
    applyAllFilters(data, segment, searchText, range, "added_on");
  };

  /** 🔹 Table Columns */
  const columns = [
    {
      title: "SI No",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Quotation Date",
      dataIndex: "date",
      render: (text) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Quotation No",
      dataIndex: "qu_no",
    },
    {
      title: "Enquiry No",
      dataIndex: "eq_no",
    },
    {
      title: "Follow Up On",
      dataIndex: "n_follow_up",
      render: (text) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
    },
    // {
    //   title: "Customer GST",
    //   dataIndex: "cus_gst",
    // },
    {
      title: "Customer Phone",
      dataIndex: "customer_phone",
    },
    // {
    //   title: "Collected / Pending",
    //   key: "payment_summary",
    //   render: (_, record) => {
    //     const payments = record.payments || [];
    //     const totalCollected = payments.reduce(
    //       (sum, p) => sum + Number(p.paid_amt || 0),
    //       0
    //     );
    //     const grandTotal = Number(payments[0]?.grand_total || 0);
    //     const pending = grandTotal - totalCollected;

    //     return (
    //       <>
    //         <div>
    //           Collected: <strong>{totalCollected}</strong>
    //         </div>
    //         <div>
    //           Pending: <strong>{pending}</strong>
    //         </div>
    //       </>
    //     );
    //   },
    // },
    // {
    //   title: "Payment Status",
    //   align: "center",
    //   render: (_, record) => {
    //     const payments = record.payments || [];
    //     const isPaid = payments.some((p) => p.status === "Paid");
    //     return (
    //       <Tag
    //         style={{ fontSize: "16px", padding: "6px 12px" }}
    //         color={isPaid ? "green" : "orange"}
    //       >
    //         {isPaid ? "Paid" : "Pending"}
    //       </Tag>
    //     );
    //   },
    // },
    {
      title: 'Updates', dataIndex: 'added_on', key: 'added_on', render: (_, record) => (
        <div>
          <div>
            <strong>Added On:</strong>{" "}
            {record.added_on
              ? dayjs(record.added_on).format("DD-MM-YYYY hh:mm A")
              : "-"}
          </div>
          <div>
            <strong>Updated On:</strong>{" "}
            {record.updated_on
              ? dayjs(record.updated_on).format("DD-MM-YYYY hh:mm A")
              : "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => (
        <Select
          size="large"
          defaultValue={status}
          style={{ width: "100px" }}
          options={[
            { label: "Active", value: "Active" },
            { label: "Draft", value: "Draft" },
            { label: "Archive", value: "Archive" },
          ]}
          onChange={async (value) => {
            try {
              const payload = { status: value };
              const response = await QuotationService.patchQuotation(
                payload,
                record.qu_no
              );
              if (response.status) {
                message.success(response.message);
                getAllQuo();
              } else {
                message.warning(response.message);
              }
            } catch (error) {
              console.error(error);
            }
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Button
          onClick={() =>
            navigate("/quotation/edit", { state: { qu_no: record.qu_no } })
          }
        >
          View
        </Button>
      ),
    },
  ];

  const clearDateFilters = () => {
    setDateRange(null);
    setDateFilterType(null); // or "updated_on" if you want default
    applyAllFilters(data, segment, searchText, null, null);
  };

  const [activeFilter, setActiveFilter] = useState({ type: 'quote', value: 'all' });

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}


        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Manage Quotation
          </Typography.Title>

          <Button
            onClick={() => navigate("/quotation/create")}
            size="large"
            type="primary"
          >
            Create Quotation
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxHeight: "70vh", // adjust as needed
          overflowY: "auto",
          paddingRight: 8, // avoids scrollbar overlap
        }}
      >
        {msgContext}

        {/* 🔹 Segment Tabs */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: '20px' }}>
          <Segmented
            value={segment}
            size="large"
            block
            onChange={handleSegmentChange}
            options={[
              {
                label: (
                  <span style={{ color: "green" }}>
                    Active <Badge count={statusCounts["Active"] || 0} style={styles.badge} />
                  </span>
                ),
                value: "Active",
              },
              {
                label: (
                  <span style={{ color: "red" }}>
                    Archive <Badge count={statusCounts["Archive"] || 0} style={styles.badge} />
                  </span>
                ),
                value: "Archive",
              },
              {
                label: (
                  <span style={{ color: "blue" }}>
                    Draft <Badge count={statusCounts["Draft"] || 0} style={styles.badge} />
                  </span>
                ),
                value: "Draft",
              },
            ]}
            style={{ fontSize: 16, fontWeight: 500, width: '50%', padding: '6px' }}
          />
        </div>


        <DashboardSummary
          quickStats={quickStats}
          followUpStats={followUpStats}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          applyQuickDateFilter={applyQuickDateFilter}
          applyFollowUpDateFilter={applyFollowUpDateFilter}
          clearDateFilters={clearDateFilters}
        />

        <Divider />

        {/* 🔹 Search + Date Range */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
            paddingBottom: '20px'
          }}
        >
          <Form.Item label="Search" layout="vertical">
            <Input
              allowClear
              placeholder="Search Quotations..."
              size="large"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 450 }}
            />
          </Form.Item>

          <Form.Item label="Date Filter" layout="vertical">
            <Space.Compact block>
              <Select
                size="large"
                allowClear
                placeholder="Date type"
                value={dateFilterType}
                onChange={(val) => {
                  setDateFilterType(val);
                  handleDateFilterTypeChange(val);
                }}
                style={{ width: "40%" }}
                options={[
                  { label: "Quotation Date", value: "date" },
                  { label: "Added On", value: "added_on" },
                  { label: "Updated On", value: "updated_on" },
                  { label: "Follow ups", value: "n_follow_up" },
                ]}
              />

              <DatePicker.RangePicker
                size="large"
                value={dateRange}
                onChange={handleDateRangeChange}
                format="DD-MM-YYYY"
                style={{ width: "60%" }}
              />
            </Space.Compact>
          </Form.Item>
        </div>

        {/* 🔹 Table */}
        <Table
          scroll={{ x: "max-content" }}
          dataSource={filteredData}
          columns={columns}
          rowKey="qu_no"
          pagination={{ pageSize: 10 }}
        />
      </div>

    </Card>
  );
};

export default QuotationPage;
